/**
 * VÉRAULT Travels — Command Palette (Phase 5)
 *
 * Global Cmd+K / Ctrl+K search across clients, trips, bookings.  Mounted at the App root so
 * it works anywhere in the advisor portal.  Powered by GET /api/ai/search.
 *
 * Behavior:
 *   - Cmd+K (Mac) / Ctrl+K (Win/Linux) toggles the modal
 *   - Esc closes
 *   - Up/Down arrows + Enter to navigate
 *   - Click a result → navigates and closes
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEBOUNCE_MS = 220;

export default function CommandPalette() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState({ clients: [], trips: [], bookings: [], forms: [], journal: [], media: [] });
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Only available to authenticated advisors / developers.  Clients should never see it.
  const enabled = user && user.role !== 'client';

  // Cmd+K / Ctrl+K toggles; Esc closes.  Global listener.
  // Also opens on the custom event dispatched by the new PortalShell search button.
  useEffect(() => {
    if (!enabled) return undefined;
    const handler = (e) => {
      const cmd = e.metaKey || e.ctrlKey;
      if (cmd && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    const openHandler = () => setOpen(true);
    window.addEventListener('keydown', handler);
    window.addEventListener('verault:open-command-palette', openHandler);
    return () => {
      window.removeEventListener('keydown', handler);
      window.removeEventListener('verault:open-command-palette', openHandler);
    };
  }, [enabled, open]);

  // Auto-focus input when modal opens.
  useEffect(() => {
    if (open) {
      setQ('');
      setResults({ clients: [], trips: [], bookings: [], forms: [], journal: [], media: [] });
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Debounced search.
  useEffect(() => {
    if (!open) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim()) {
      setResults({ clients: [], trips: [], bookings: [], forms: [], journal: [], media: [] });
      return undefined;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        // Pass D — unified ecosystem search across all surfaces
        const res = await axios.get(`${API}/search`, { params: { q }, withCredentials: true });
        const r = res.data?.results || {};
        // Normalize to the legacy shape the UI expects + add new types
        setResults({
          clients: (r.clients || []).map((c) => ({ id: c.id, full_name: c.title, email: c.subtitle, _url: c.url })),
          trips: (r.journeys || []).map((j) => ({
            id: j.id, internal_trip_name: j.title, client_name: j.subtitle, destinations: j.meta, _url: j.url
          })),
          bookings: (r.bookings || []).map((b) => ({
            id: b.id, vendor: b.title, description: b.subtitle, booking_type: b.meta, _url: b.url
          })),
          forms: (r.forms || []).map((f) => ({ id: f.id, name: f.title, description: f.subtitle, _url: f.url })),
          journal: (r.journal || []).map((j) => ({ id: j.id, title: j.title, excerpt: j.subtitle, _url: j.url })),
          media: (r.media || []).map((m) => ({ id: m.id, alt_text: m.title, caption: m.subtitle, _url: m.url })),
        });
        setActiveIdx(0);
      } catch (_) {
        setResults({ clients: [], trips: [], bookings: [], forms: [], journal: [], media: [] });
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q, open]);

  // Flat list of results so up/down + enter can iterate consistently across entity types.
  const flat = [
    ...results.clients.map(c => ({ kind: 'client', id: c.id, label: c.full_name, sub: c.email, to: c._url || `/portal/advisor/clients/${c.id}` })),
    ...results.trips.map(t => ({ kind: 'journey', id: t.id, label: t.internal_trip_name, sub: t.client_name, to: t._url || `/portal/advisor/journeys/${t.id}` })),
    ...(results.bookings || []).map(b => ({ kind: 'booking', id: b.id, label: b.vendor, sub: b.description, to: b._url || '/portal/advisor/bookings' })),
    ...(results.forms || []).map(f => ({ kind: 'form', id: f.id, label: f.name, sub: f.description, to: f._url || '/portal/advisor/forms' })),
    ...(results.journal || []).map(j => ({ kind: 'journal', id: j.id, label: j.title, sub: j.excerpt, to: j._url || `/advisor/content/journal/${j.id}` })),
    ...(results.media || []).map(m => ({ kind: 'media', id: m.id, label: m.alt_text, sub: m.caption, to: m._url || '/portal/advisor/content/media' })),
  ];

  const choose = useCallback((item) => {
    if (!item) return;
    setOpen(false);
    navigate(item.to);
  }, [navigate]);

  const onKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(flat[activeIdx]);
    }
  };

  if (!enabled || !open) return null;

  return (
    <div
      className="fixed inset-0 z-[9990] bg-black/30 backdrop-blur-[6px] flex items-start justify-center pt-[14vh] px-4 animate-in fade-in duration-200"
      onClick={() => setOpen(false)}
      data-testid="command-palette-backdrop"
    >
      <div
        className="w-full max-w-xl rounded-2xl bg-[#FAFAF7] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.25)] overflow-hidden border border-black/5"
        onClick={(e) => e.stopPropagation()}
        data-testid="command-palette"
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[#E8E4DC]">
          <Search className="w-4 h-4 text-[#C5A059]" strokeWidth={1.5} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="What can I bring you?"
            data-testid="command-palette-input"
            className="flex-1 bg-transparent font-body text-[15px] text-[#1A1A1A] placeholder-[#8C8C8C] focus:outline-none"
          />
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8C8C8C]">
            {loading ? '…' : flat.length ? `${flat.length} found` : ''}
          </span>
          <kbd className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#8C8C8C] bg-[#F0EDE6] px-1.5 py-0.5 rounded">esc</kbd>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto py-2">
          {flat.length === 0 && q.trim() && !loading && (
            <p className="px-5 py-6 font-accent italic text-sm text-[#8C8C8C] text-center">
              Nothing in the archives for "{q}" — try a name, destination, or confirmation number.
            </p>
          )}
          {flat.length === 0 && !q.trim() && (
            <p className="px-5 py-6 font-accent italic text-sm text-[#8C8C8C] text-center">
              Search clients, trips, bookings, vendors, or confirmations — anything in the studio.
            </p>
          )}
          {renderGroup('Clients', results.clients.length, flat, 'client', activeIdx, choose)}
          {renderGroup('Journeys', results.trips.length, flat, 'journey', activeIdx, choose)}
          {renderGroup('Bookings', results.bookings.length, flat, 'booking', activeIdx, choose)}
          {renderGroup('Forms', (results.forms || []).length, flat, 'form', activeIdx, choose)}
          {renderGroup('Journal', (results.journal || []).length, flat, 'journal', activeIdx, choose)}
          {renderGroup('Media', (results.media || []).length, flat, 'media', activeIdx, choose)}
        </div>
      </div>
    </div>
  );
}

function renderGroup(title, count, flat, kind, activeIdx, choose) {
  if (count === 0) return null;
  return (
    <div className="px-2 pb-2">
      <p className="px-3 pt-2 pb-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[#8C8C8C]">{title}</p>
      {flat.map((item, i) => {
        if (item.kind !== kind) return null;
        const active = i === activeIdx;
        return (
          <button
            key={`${item.kind}-${item.id}`}
            type="button"
            onClick={() => choose(item)}
            data-testid={`cmd-result-${item.kind}-${item.id}`}
            className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center gap-3 transition-colors duration-150 ${
              active ? 'bg-[#1A1A1A] text-white' : 'hover:bg-[#F0EDE6] text-[#1A1A1A]'
            }`}
          >
            <span aria-hidden className={`size-1 rounded-full ${active ? 'bg-[#C5A059]' : 'bg-[#C5A059]/40'}`} />
            <div className="flex-1 min-w-0">
              <p className={`text-[14px] truncate ${active ? 'font-serif' : 'font-body'}`}>{item.label}</p>
              {item.sub && <p className={`text-[11px] truncate font-body ${active ? 'text-white/60' : 'text-[#8C8C8C]'}`}>{item.sub}</p>}
            </div>
            <span className={`font-mono text-[10px] uppercase tracking-[0.22em] ${active ? 'text-white/50' : 'text-[#8C8C8C]'}`}>{item.kind}</span>
          </button>
        );
      })}
    </div>
  );
}
